// Scanner.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Base from "../Config/Base";
import { Footer } from "../Pages/footer/footer";
import Swal from "sweetalert2";
import { BarcodeDetector } from "barcode-detector";
import { message } from "antd";
import { API_BASE_URL } from "../Config/Config";

export default function Scanner() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [isScannerCollapsed, setIsScannerCollapsed] = useState(false); // ← Changed: false by default → open on load
  const [scannedData, setScannedData] = useState(null);
  const [rawScannedValue, setRawScannedValue] = useState(null);
  const [error, setError] = useState(null);
  const [userDataSession, setUserDataSession] = useState({});
  const [editedWeight, setEditedWeight] = useState("0"); // Changed: Default to "0" instead of empty string

  // Load user session
  useEffect(() => {
    const userDataString =
      sessionStorage.getItem("MisUserDetails") ||
      localStorage.getItem("MisUserDetails");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserDataSession(userData);
    } else {
      console.log("User data not found in sessionStorage");
      navigate("/");
    }
  }, []);
  console.log("User session data:", userDataSession, Date.now());
  // Auto-start scanner when component mounts (only once)
  useEffect(() => {
    if (!isScannerCollapsed) {
      startScan();
    }
    return () => {
      stopCamera();
    };
  }, []); // Empty dependency → runs only on mount

  const startScan = async () => {
    if (videoRef.current?.srcObject) {
      stopCamera();
    }
    if (scanning) return;

    setScanning(true);
    setError(null);
    setScannedData(null);
    setRawScannedValue(null);
    setEditedWeight("0");

    try {
      if (!("BarcodeDetector" in window)) {
        throw new Error("BarcodeDetector not supported in this browser");
      }

      const detector = new BarcodeDetector({
        formats: ["qr_code", "ean_13", "code_128", "code_39", "pdf417"],
      });

      console.log("Requesting camera stream...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;

        const playPromise = videoRef.current.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => console.log("Video started playing"))
            .catch((playErr) => {
              console.warn("Initial play interrupted:", playErr);
              setTimeout(() => {
                if (videoRef.current && videoRef.current.paused) {
                  videoRef.current.play().catch((retryErr) => {
                    console.error("Retry failed:", retryErr);
                    setError("Could not start video. Tap screen or restart.");
                  });
                }
              }, 800);
            });
        }

        videoRef.current.oncanplay = () => {
          console.log("Video ready → starting detection");
          detectBarcodes(detector);
        };

        videoRef.current.onerror = (e) => {
          console.error("Video error:", e);
          setError("Video error. Restart scan.");
          stopCamera();
        };
      }
    } catch (err) {
      console.error("startScan failed:", err);
      setError(err.message || "Failed to start camera.");
      setScanning(false);
    }
  };

  const fetchInventoryData = async (barcode) => {
    try {
      const waitForOrgId = () =>
        new Promise((resolve, reject) => {
          const start = Date.now();
          const check = setInterval(() => {
            if (userDataSession?.OrgId) {
              clearInterval(check);
              resolve(userDataSession.OrgId);
            } else if (Date.now() - start > 5000) {
              clearInterval(check);
              reject(new Error("OrgId timeout"));
            }
          }, 100);
        });

      const orgId = await waitForOrgId();

      console.log("API called with OrgId:", orgId, "Barcode:", barcode);

      const response = await fetch(
        `${API_BASE_URL}Inventory/getInventoryProdByCode`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            OrgId: orgId,
            BarCode: barcode, // Use local variable – always correct
          }),
        },
      );

      const data = await response.json();
      Swal.close();

      if (data.Status && data.ResultData?.length > 0) {
        const apiData = data.ResultData[0];

        const mappedData = {
          invoiceNo: apiData.BatchNo || "N/A",
          inwardDate: apiData.ExpiryDate
            ? new Date(apiData.ExpiryDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
          productName: apiData.ProductName || "Unknown Product",
          quantity: apiData.Quantity || 0,
          unit: apiData.UnitName || "N/A",
          weight: apiData.Weight?.toFixed(1) || "0.0",
          remainingQuantity: apiData.RemainingQuantity?.toFixed(1) || "N/A",
          id: apiData.Id,
          orgId: apiData.OrgId,
          productId: apiData.ProductId,
          storeId: apiData.StoreId,
          variantId: apiData.VariantId,
          batchNo: apiData.BatchNo,
          mfgDate: apiData.MfgDate,
          expiryDate: apiData.ExpiryDate,
        };

        setScannedData(mappedData);
        setEditedWeight(mappedData.weight || "0");
        message.success("Data loaded from inventory");
      } else {
        setScannedData(null);
        Swal.fire({
          title: "Invalid Barcode",
          text: "No inventory record found for this barcode. Please scan again.",
          icon: "error",
          confirmButtonText: "Scan Again",
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            toggleScanner();
          }
        });
      }
    } catch (err) {
      console.error("Fetch error:", err);
      Swal.close();
      message.error("Failed to fetch data");
      setScannedData(null);
    }
  };

  const detectBarcodes = async (detector) => {
    if (!videoRef.current) return;
    try {
      const barcodes = await detector.detect(videoRef.current);

      if (barcodes.length > 0) {
        const rawValue = barcodes[0].rawValue?.trim();

        if (rawValue && rawValue.length > 0) {
          console.log("Scanned barcode (raw):", rawValue);

          // Immediately store the scanned value (for UI)
          setRawScannedValue(rawValue);

          // Stop camera right away
          stopCamera();

          // Show loading popup
          Swal.fire({
            title: "Fetching data...",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });

          // Use local rawValue directly – no state dependency
          fetchInventoryData(rawValue);
        } else {
          message.warning("Empty or invalid barcode detected");
        }
      }

      // Keep the loop going if scanning is still active
      if (scanning && videoRef.current?.srcObject) {
        setTimeout(() => detectBarcodes(detector), 200);
      }

      if (videoRef.current?.srcObject) {
        setTimeout(() => detectBarcodes(detector), 200);
      }
    } catch (err) {
      console.error("Detection error:", err);
      setError("Detection failed. Try again.");
      stopCamera();
    }
  };

  const handleSave = async () => {
    if (!scannedData || !editedWeight || parseFloat(editedWeight) <= 0) {
      message.warning("Please enter a valid weight greater than 0");
      return;
    }

    const payload = {
      OrgId: scannedData.orgId,
      ProductId: scannedData.productId,
      StoreId: scannedData.storeId,
      VariantId: scannedData.variantId,
      Quantity: scannedData.quantity,
      WeightIncrement: parseFloat(editedWeight),
      BatchNo: scannedData.batchNo || null,
      IsActive: true,
      MfgDate: scannedData.mfgDate,
      ExpiryDate: scannedData.expiryDate,
      CreatedBy: userDataSession?.UserId,
      UpdatedBy: userDataSession?.UserId,
      Id: scannedData.id,
    };

    Swal.fire({
      title: "Saving...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}Inventory/UpdateInventoryProduct`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      Swal.close();

      if (result.status === 200 || result.Status) {
        message.success("Weight updated successfully!");
        setScannedData(null);
        setRawScannedValue(null);
        setEditedWeight("0");
        toggleScanner(); // Ensures camera turns off after successful API call
      } else {
        message.error(result.Message || "Failed to update inventory");
      }
    } catch (err) {
      console.error("Save error:", err);
      Swal.close();
      message.error("Server error while saving");
    }
  };

  const stopCamera = () => {
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  };

  const toggleScanner = () => {
    setIsScannerCollapsed(!isScannerCollapsed);
    if (isScannerCollapsed) {
      startScan();
    } else {
      stopCamera();
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <Base>
      <div className="page-header">
        <div className="page-title">
          <h3>Scan Barcode</h3>
          <h6>Inward Inventory</h6>
        </div>
      </div>

      <div className="card-body pb-5 mb-5">
        {/* Scanner Toggle Button */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Scanner</h5>
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
            onClick={toggleScanner}
            title={isScannerCollapsed ? "Open Scanner" : "Close Scanner"}
          >
            <i
              className={`bi ${isScannerCollapsed ? "bi-upc-scan" : "bi-x-circle"} fs-5`}
            ></i>
            {isScannerCollapsed ? "Start " : "Stop "}
          </button>
        </div>

        {/* Camera container ONLY when scanner is active */}
        {!isScannerCollapsed && (
          <div className="scanner-container position-relative mb-4 border rounded overflow-hidden bg-dark">
            {scanning ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "400px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <></>
            )}

            {/* {scanning && (
              <div
                className="position-absolute top-50 start-50 translate-middle"
                style={{
                  width: "220px",
                  height: "140px",
                  border: "2px dashed #00ff00",
                  borderRadius: "12px",
                  pointerEvents: "none",
                }}
              ></div>
            )} */}
          </div>
        )}

        {error && <div className="alert alert-danger mb-4">{error}</div>}

        {/* Raw scanned value – hidden as per your preference */}
        {rawScannedValue && (
          <div className="alert alert-secondary mb-3 text-center d-none">
            <strong>Scanned barcode:</strong> {rawScannedValue}
          </div>
        )}

        {/* Result Card */}
        {scannedData && (
          <div className="card shadow-sm border-0 bg-light mt-4">
            <div className="card-body">
              <h5 className="card-title text-success mb-4 d-none">
                Scan Successful
              </h5>

              <div className="mb-3 p-2 bg-white border rounded text-center d-none">
                <small className="text-muted d-block">Raw Scanned Value</small>
                <strong className="fs-5">{rawScannedValue}</strong>
              </div>

              <div className="row g-3">
                <div className="col-6 col-md-4">
                  <small className="text-muted">Invoice No</small>
                  <p className="fw-bold mb-0">{scannedData.invoiceNo}</p>
                </div>
                <div className="col-6 col-md-4">
                  <small className="text-muted">Inward Date</small>
                  <p className="fw-bold mb-0">{scannedData.inwardDate}</p>
                </div>
                <div className="col-12 col-md-4">
                  <small className="text-muted">Product Name</small>
                  <p className="fw-bold mb-0">{scannedData.productName}</p>
                </div>
                <div className="col-6 col-md-4">
                  <small className="text-muted">Quantity</small>
                  <p className="fw-bold mb-0">
                    {scannedData.quantity} {scannedData?.unit}
                  </p>
                </div>
                <div className="col-6 col-md-4 d-none">
                  <small className="text-muted">Unit</small>
                  <p className="fw-bold mb-0">{scannedData.unit}</p>
                </div>
                <div className="col-6 col-md-4">
                  <small className="text-muted">Remaining </small>
                  <p className="fw-bold mb-0">
                    {scannedData.remainingQuantity} {scannedData.unit}
                  </p>
                </div>
                <div className="col-6 col-md-4">
                  <small className="text-muted">Outward Qty</small>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    className="form-control"
                    value={editedWeight}
                    onChange={(e) => setEditedWeight(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4 text-end">
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={() => {
                    setScannedData(null);
                    setRawScannedValue(null);
                    setEditedWeight("0");
                    toggleScanner();
                  }}
                >
                  Scan Again
                </button>
                <button className="btn btn-success" onClick={handleSave}>
                  Save to Inventory
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hint when scanning but no result yet */}
        {scanning && !scannedData && !rawScannedValue && (
          <div className="alert alert-info mt-3 text-center">
            Point the camera at a valid barcode
          </div>
        )}
      </div>

      {/* Fixed action bar (hidden) */}
      <div
        style={{
          position: "fixed",
          bottom: "80px",
          left: 0,
          right: 0,
          background: "white",
          borderTop: "1px solid #dee2e6",
          padding: "12px 20px",
          boxShadow: "0 -3px 12px rgba(0,0,0,0.08)",
          zIndex: 999,
        }}
        className="d-none"
      >
        <div className="d-flex justify-content-end gap-3">
          <button
            className="btn btn-outline-secondary px-4 py-2"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>

      <Footer />
    </Base>
  );
}
